import React, { useEffect, ReactNode, useCallback, useRef } from 'react';
import { useLocation, useMatches } from 'react-router-dom';
import { container, SERVICE_IDENTIFIERS } from '@core/di/ServiceContainer';
import { IAnalyticsService } from '@core/interfaces';
import { getAnalyticsService, useAnalytics } from '@core/contexts/ServiceContext.tsx';

interface AnalyticsProviderProps {
    children: ReactNode;
}

/**
 * Analytics Provider Component
 * Wraps your app to provide Analytics functionality via context
 */
// TODO: Refactor flow, to intercept at API call level for more accurate tracking
export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
    useEffect(() => {
        const analyticsService = getAnalyticsService();

        // Initialize analytics service
        if (!analyticsService.isInitialized()) {
            analyticsService.initialize();
        }

        // Cleanup on unmount
        return () => {
            const analyticsService = getAnalyticsService();
            analyticsService.cleanup();
        };
    }, []);

    return <>{children}</>;
};

/**
 * Route Tracking Component
 */
export const AnalyticsRouteTracker: React.FC = () => {
    const location = useLocation();
    const matches = useMatches() as any[];

    // Cache service reference
    const analyticsServiceRef = useRef<IAnalyticsService | null>(null);
    const trackingTimeoutRef = useRef<number | null>(null);
    const previousPageRef = useRef<{ name: string; url: string } | null>(null);

    // Initialize service once
    if (!analyticsServiceRef.current) {
        try {
            analyticsServiceRef.current = container.get<IAnalyticsService>(SERVICE_IDENTIFIERS.ANALYTICS);
        } catch (error) {
            console.warn('[AnalyticsRouteTracker] Service not available');
        }
    }

    useEffect(() => {
        const analyticsService = analyticsServiceRef.current;
        if (!analyticsService) return;

        // Clear any pending tracking
        if (trackingTimeoutRef.current) {
            clearTimeout(trackingTimeoutRef.current);
        }

        // Get current page info immediately (synchronous)
        const currentMatch = matches[matches.length - 1];
        const name = currentMatch?.handle?.name || 'Unknown Page';
        const pageName = `(React App) / ${name}`;
        const url = `${window.location.protocol}//${window.location.host}${location.pathname}${location.search}`;

        // Stop previous page tracking immediately (non-blocking)
        if (previousPageRef.current) {
            const prevPage = previousPageRef.current;

            // Use requestIdleCallback for non-critical work
            if ('requestIdleCallback' in window) {
                requestIdleCallback(() => {
                    analyticsService.stopTrackPage(prevPage.name, prevPage.url);
                });
            } else {
                // Fallback to setTimeout with 0 delay (next tick)
                setTimeout(() => {
                    analyticsService.stopTrackPage(prevPage.name, prevPage.url);
                }, 0);
            }
        }

        // Start tracking new page in background
        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
                analyticsService.startTrackPage(pageName, url);
            });
        } else {
            setTimeout(() => {
                analyticsService.startTrackPage(pageName, url);
            }, 0);
        }

        // Update reference
        previousPageRef.current = { name: pageName, url };

        // Cleanup function
        return () => {
            if (trackingTimeoutRef.current) {
                clearTimeout(trackingTimeoutRef.current);
            }
        };
    }, [location.pathname, location.search, matches]);

    return null;
};

/**
 * Hook to track component mount/unmount
 * Useful for tracking component lifecycle
 */
export const useComponentTracking = (componentName: string, properties?: Record<string, any>) => {
    const { trackEvent } = useAnalytics();

    useEffect(() => {
        // Track component mount
        trackEvent(`${componentName}_Mounted`, {
            ...properties,
            componentName,
            timestamp: new Date().toISOString()
        });

        // Track component unmount
        return () => {
            trackEvent(`${componentName}_Unmounted`, {
                ...properties,
                componentName,
                timestamp: new Date().toISOString()
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [componentName]); // Only run on mount/unmount
};

/**
 * Hook to track errors in a component
 * Wraps error tracking with component context
 */
export const useErrorTracking = (componentName: string) => {
    const { trackException } = useAnalytics();

    const trackError = useCallback(
        (error: Error | unknown, additionalProperties?: Record<string, any>) => {
            trackException(error, {
                componentName,
                ...additionalProperties
            });
        },
        [componentName, trackException]
    );

    return { trackError };
};

/**
 * Hook to track user actions
 * Simplified event tracking with automatic component context
 */
export const useActionTracking = (componentName: string) => {
    const { trackEvent } = useAnalytics();

    const trackAction = useCallback(
        (actionName: string, properties?: Record<string, any>, measurements?: Record<string, number>) => {
            trackEvent(
                actionName,
                {
                    componentName,
                    ...properties
                },
                measurements
            );
        },
        [componentName, trackEvent]
    );

    return { trackAction };
};
